import { FunctionComponent, useMemo, useState } from "react";
import { yupResolver } from "@hookform/resolvers/yup";
import { Controller } from "react-hook-form";
import * as yup from "yup";
import { Alert, Icon, Link, styled, Typography } from "@mui/material";
import { useApiClientContext } from "../../app/ApiClientCtx";
import { replaceBracesKey } from "../../helpers/formatter";
import { buildUrlWithQueryParams } from "../../dataAccess/QueryParams";
import { useConfiguration } from "../../dataAccess/api/configuration";
import { forgotPassword } from "../../dataAccess/api/user";
import { useNavigateSameTab } from "../../helpers/navigation";
import TextField from "./TextField";
import { ThemeButton } from "./ThemeComponents/ThemeButton";
import {
  FormContent,
  FormSection,
  StyledForm,
  StyledFormTitle,
} from "./SignUpForm";
import { TextButton } from "./TextButton";
import { CloseButton } from "./CloseButton";
import { ButtonProgress } from "./ButtonProgress";
import { pageMapping } from "../../builder/mapping/page";
import SSOIntegration from "./SSOIntegration";
import { SignupSettings } from "../pages/SignUp";
import {
  DEFAULT_SIGN_IN_VALUE,
  DEFAULT_SIGN_UP_VALUE,
  EMAIL_ADDRESS_PLACEHOLDER_TEXT,
  FORGOT_PASSWORD_LABEL,
  NO_ACCOUNT_LABEL,
  PASSWORD_PLACEHOLDER_TEXT,
  SKIP_BUTTON_LABEL,
} from "../../../theme/defaultConstants";
import { LoginSettings } from "../pages/LogIn";
import { ForgotPasswordSettings } from "../pages/ForgotPassword";
import { useTranslation } from "../../helpers/i18n";
import { useForm } from "../../helpers/useForm";
import { PasswordField } from "./PasswordField";

interface FormData {
  email: string;
  password: string;
}

interface Props {
  onSubmit: (data: FormData) => void;
  loading?: boolean;
  serverError?: string;
  settings: LoginSettings;
  setOpenSignUpInProfilePopup?: () => void;
  closePopUp?: () => void;
}

const StyledLink = styled(Link)(() => ({
  cursor: "pointer",
}));

const ForgotPasswordButton = styled(TextButton)(() => ({
  alignSelf: "flex-start",
}));

const SignUpLink = styled(Typography)(() => ({
  alignSelf: "center",
  marginTop: "1rem",
}));

const LoginForm: FunctionComponent<Props> = ({
  loading,
  onSubmit,
  serverError,
  settings,
  setOpenSignUpInProfilePopup,
  closePopUp,
}: Props) => {
  const { t } = useTranslation();
  const { APIV3 } = useApiClientContext();
  const {
    label,
    sublabel,
    buttonLabel,
    emailAddressPlaceholderText,
    noAccountLabel,
    orLabel,
    passwordPlaceholderText,
    skipButtonLabel,
  } = settings || {};
  const emailSchemaOptions = useMemo(
    () => ({
      email: yup
        .string()
        .transform((value) => value.trim())
        .email(t("information_email_valid"))
        .required(t("information_email_required")),
    }),
    [t],
  );

  const passwordSchemaOptions = useMemo(
    () => ({
      password: yup.string().required(t("information_password_required")),
    }),
    [t],
  );

  const forgotPasswordSchema = useMemo(
    () =>
      yup.object().shape({
        ...emailSchemaOptions,
      }),
    [emailSchemaOptions],
  );

  const schema = useMemo(
    () =>
      yup.object().shape({
        ...emailSchemaOptions,
        ...passwordSchemaOptions,
      }),
    [emailSchemaOptions, passwordSchemaOptions],
  );

  const { push: navigate } = useNavigateSameTab();
  const { configuration } = useConfiguration();
  const [forgotPasswordLoading, setForgotPasswordLoading] = useState(false);
  const { control, formState, handleSubmit, getValues, setError, setFocus } =
    useForm<FormData>({
      shouldFocusError: true,
      resolver: yupResolver(schema),
      skipDefaultValidationFor: ["password"],
      defaultValues: {
        email: "",
        password: "",
      },
    });

  const onForgotPassword = async () => {
    try {
      await forgotPasswordSchema.validateSync(getValues());
    } catch (e) {
      if (e instanceof yup.ValidationError) {
        setError("email", {
          message: e.message,
          type: e.path,
        });
        setFocus("email");
      } else {
        console.error(e);
      }
      return;
    }

    setForgotPasswordLoading(true);

    await forgotPassword(APIV3, getValues("email")?.trim()).json();

    setForgotPasswordLoading(false);
    navigate(
      buildUrlWithQueryParams(pageMapping.ForgotPassword.path, {
        email: getValues("email"),
      }),
    );
  };

  const signupSettings = useMemo(
    () =>
      configuration?.publishedTemplate?.properties.pages.find(
        ({ id }) => id === "SignUp",
      )?.settings as SignupSettings,
    [configuration],
  );
  const forgotPasswordSettings = useMemo(
    () =>
      configuration?.publishedTemplate?.properties.pages.find(
        ({ id }) => id === "ForgotPassword",
      )?.settings as ForgotPasswordSettings,
    [configuration],
  );
  const signUpLabel = signupSettings?.label || DEFAULT_SIGN_UP_VALUE;
  const forgotPasswordLabel =
    forgotPasswordSettings?.label || FORGOT_PASSWORD_LABEL;
  const title = label || DEFAULT_SIGN_IN_VALUE;

  return (
    <StyledForm
      isOnPopup={!!setOpenSignUpInProfilePopup}
      onSubmit={handleSubmit(onSubmit)}
      aria-label={title}
    >
      {closePopUp && (
        <CloseButton onClick={closePopUp}>
          <Icon>close</Icon>
        </CloseButton>
      )}
      <FormContent isOnPopup={!!setOpenSignUpInProfilePopup}>
        <StyledFormTitle variant="h1">{title}</StyledFormTitle>
        {sublabel && <StyledFormTitle subtitle>{sublabel}</StyledFormTitle>}
        {serverError && <Alert severity="error">{serverError}</Alert>}
        <SSOIntegration orLabel={orLabel} />
        <FormSection>
          <Controller
            name="email"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label={
                  emailAddressPlaceholderText || EMAIL_ADDRESS_PLACEHOLDER_TEXT
                }
                fullWidth
                error={!!formState.errors.email}
                helperText={
                  formState.errors.email ? formState.errors.email.message : ""
                }
                inputProps={{ autoComplete: "email" }}
                inputRef={field.ref}
              />
            )}
          />
          <Controller
            name="password"
            control={control}
            render={({ field }) => (
              <PasswordField
                {...field}
                label={passwordPlaceholderText || PASSWORD_PLACEHOLDER_TEXT}
                fullWidth
                error={!!formState.errors.password}
                helperText={
                  formState.errors.password
                    ? formState.errors.password.message
                    : ""
                }
                inputProps={{ autoComplete: "current-password" }}
                inputRef={field.ref}
              />
            )}
          />
          <ForgotPasswordButton
            startIcon={
              forgotPasswordLoading ? (
                <ButtonProgress />
              ) : (
                <Icon>lock_reset</Icon>
              )
            }
            onClick={onForgotPassword}
          >
            {forgotPasswordLabel}
          </ForgotPasswordButton>
          <ThemeButton
            startIcon={loading ? <ButtonProgress /> : null}
            variant="contained"
            type="submit"
            disabled={loading}
          >
            {buttonLabel || DEFAULT_SIGN_IN_VALUE}
          </ThemeButton>
          {configuration?.isOpenMarketplace && !setOpenSignUpInProfilePopup && (
            <ThemeButton href={pageMapping.PLP.path} variant="text" fullWidth>
              {skipButtonLabel || SKIP_BUTTON_LABEL}
            </ThemeButton>
          )}
        </FormSection>
        <SignUpLink>
          {replaceBracesKey(noAccountLabel || NO_ACCOUNT_LABEL, {
            key: "sign_up",
            value: setOpenSignUpInProfilePopup ? (
              <StyledLink onClick={setOpenSignUpInProfilePopup} tabIndex={0}>
                {signUpLabel}
              </StyledLink>
            ) : (
              <StyledLink href={pageMapping.SignUp.path} tabIndex={0}>
                {signUpLabel}
              </StyledLink>
            ),
          })}
        </SignUpLink>
      </FormContent>
    </StyledForm>
  );
};

export default LoginForm;
